import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ChevronIcon,
  CycleIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from '@/components/catalog/icons';
import { CycleFormModal } from '@/components/cycles/CycleFormModal';
import { ApiError, api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { formatDateOnly, toDateInputValue } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type { Cycle, CycleListSort, Paginated } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import { useTableFilters, type ColumnSpec } from '@/lib/use-table-filters';
import {
  Button,
  ColumnManager,
  ConfirmDialog,
  EmptyState,
  FilterableTH,
  type FilterableTHProps,
  type FrozenSide,
  PageHeader,
  Panel,
  PanelFooter,
  PanelHeader,
  Skeleton,
  type SortDirection,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

const PER_PAGE = 20;
const SKELETON_ROWS = 6;

/*
 * 🔴 SORT IS SERVER-SIDE NOW — 2026-10-09. Every column with a funnel offers
 * Ascending / Descending, held in the URL as `?sort=` / `?order=` and sent as
 * `sort` / `order` on the list request (members: `CYCLE_SORT_MEMBERS`). With no
 * sort chosen NOTHING is sent, so the server's own default order is unchanged.
 * The filters are still page-scoped; only the "no sort" half below is dead.
 *
 * ⚠️ *SUPERSEDED 2026-10-09, the sort half of the paragraph below: "Cycles have
 * no search, no filter and no sort server-side … ordering is always `id DESC`
 * … **no column declares a `sortKey`** (which is what stops `FilterableTH`
 * rendering sort controls at all)".*
 *
 * Cycles have no search, no filter and no sort server-side — `page` and
 * `perPage` are the only accepted query params, measured against
 * `listCyclesQuerySchema` in the backend rather than inferred from a 200, and
 * ordering is always `id DESC`. So this panel deliberately carries no search
 * box, **no column declares a `sortKey`** (which is what stops `FilterableTH`
 * rendering sort controls at all), and **every column filter here is
 * page-scoped permanently.** This is the one catalog list with nothing to
 * convert: not columns awaiting a `param`, but a whole endpoint the API offers
 * no way to narrow. Any key added here would be an invented one.
 *
 * ⚠️ This used to end "the popover says outright that it filters the rows
 * loaded here, not the whole table". **It no longer does** — the scope footnote
 * was removed from `ColumnFilterPanel` on 2026-09-05 under the owner's
 * helper-text rule, so nothing in the popover now distinguishes a page filter
 * from a server one. `PageFilterEmptyState` below is the only surviving place
 * that says it, and it says it only once every row has been hidden. Do not
 * restore a sentence here to compensate: that ruling outranks this one.
 *
 * Dates are grouped and labelled by their bare 'YYYY-MM-DD' form rather than a
 * localised one. The option list sorts by label and ISO-8601 is the one date
 * format that sorts chronologically as plain text; it also keeps these values
 * strings end to end, which is the rule for the catalog — `new Date(
 * '2026-05-31')` is midnight UTC and renders as 30 May west of Greenwich.
 */
const CYCLE_COLUMNS: ColumnSpec<Cycle>[] = [
  { key: 'cycleName', label: 'Cycle', get: (row) => row.cycleName },
  { key: 'startDate', label: 'Starts', get: (row) => row.startDate },
  { key: 'endDate', label: 'Ends', get: (row) => row.endDate },
  {
    key: 'createdAt',
    label: 'Created',
    // Grouped by day: `createdAt` is a real timestamp, so full precision would
    // make every row its own distinct value.
    get: (row) => toDateInputValue(row.createdAt),
  },
  {
    key: 'modifiedBy',
    label: 'Last edited',
    /*
     * `modifiedBy` rather than `modifiedAt`, so "Is empty" means exactly
     * "never edited since insert" — the one reliable untouched signal on the
     * catalog. The distinct values are the opaque provenance strings, which is
     * also why they are never resolved against /users.
     */
    get: (row) => row.modifiedBy,
  },
];

/**
 * Column key → the `/cycles` sort member it orders by. Every funnel column has
 * one. The URL is the store (`?sort=` / `?order=`); the hook's own sort state is
 * not used, because it would not survive a reload or a shared link.
 */
const CYCLE_SORT_MEMBERS = {
  cycleName: 'cycleName',
  startDate: 'startDate',
  endDate: 'endDate',
  createdAt: 'createdAt',
  modifiedBy: 'modifiedBy',
} satisfies Partial<Record<string, CycleListSort>>;

const SORT_MEMBER_BY_KEY: Partial<Record<string, CycleListSort>> = CYCLE_SORT_MEMBERS;
const SORT_MEMBERS: ReadonlySet<string> = new Set(Object.values(CYCLE_SORT_MEMBERS));

/** The URL's sort, or `null` when none (or an unrecognised one) is named. */
function parseSort(params: URLSearchParams): { key: CycleListSort; direction: SortDirection } | null {
  const raw = params.get('sort');
  if (raw === null || !SORT_MEMBERS.has(raw)) return null;
  return { key: raw as CycleListSort, direction: params.get('order') === 'desc' ? 'desc' : 'asc' };
}

/**
 * The row-actions lane. It is not a `ColumnSpec` — there is nothing on it to
 * filter — so it is named once here and carried in `COLUMN_KEYS` alongside the
 * five that are.
 */
const ACTIONS_KEY = 'actions';

/**
 * Every column this table renders, in its DECLARED order, and the ONE list the
 * header, the body, the skeleton and the width table all walk.
 *
 * ⚠️ **One array, four consumers, on purpose.** Before this the header, the body
 * and `TableSkeleton` each wrote their own cells out longhand, so a column could
 * exist in two of the three and the only symptom was a table whose skeleton
 * jumped sideways when the data landed — which is exactly what the pinned-column
 * comment below already warns about, one column at a time. A key absent from one
 * consumer is now a key absent from all of them.
 */
const COLUMN_KEYS: readonly string[] = [
  'cycleName',
  'startDate',
  'endDate',
  'createdAt',
  'modifiedBy',
  ACTIONS_KEY,
];

/**
 * Actions may never be hidden or moved.
 *
 * ⚠️ **Module-level, not an inline literal.** `useColumnLayout` memoises the
 * locked set on the array's identity, so a fresh `['actions']` every render
 * would rebuild it every render.
 */
const LOCKED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * Keys this table renders with `pinned` rather than with `frozen`.
 *
 * ⚠️ **NOT the same set as `LOCKED_KEYS`, even though they hold the same one key
 * here.** Locked is about hiding and reordering; pinned is about which edge a
 * cell already occupies. They coincide on this table because its only locked
 * column is also its only pinned one — on a page that locks its identifying
 * column too they come apart, and conflating them makes that column unfreezable.
 *
 * It is what tells `useFrozenOffsets` that the right edge is already taken:
 * `pinned` is `sticky right-0` and appears in no freeze list, so unmeasured the
 * first right-frozen column is handed offset 0 and the two stack, one hidden
 * behind the other with nothing erroring. It also withdraws the freeze buttons
 * from this row in `ColumnManager`, because that edge is not the reader's to
 * choose.
 *
 * ⚠️ **Module-level for the same reason as `LOCKED_KEYS`** — both hooks memoise
 * the set on the array's identity, so a fresh literal every render rebuilds it
 * every render.
 */
const PINNED_KEYS: readonly string[] = [ACTIONS_KEY];

/**
 * The px each column contributes to the table's minimum width.
 *
 * 🔴 **THIS REPLACES AN 880px `min-w-` LITERAL WRITTEN OUT THREE TIMES, AND THE
 * REASON IT HAD TO IS THE POINT: a fixed floor does not shrink when a column is
 * hidden.** Hide four of six and the table still claims 880px, so the survivors
 * are stretched across a scroller that still scrolls — the reader hides columns
 * to fit the table on screen and it does not get any narrower.
 *
 * ⛔ **The sum goes through `style`, NEVER a `min-w-[…]` class.** Tailwind v4
 * scans SOURCE TEXT, so a computed class emits no rule at all: the attribute
 * lands in the DOM, nothing applies it, and the table collapses to its content
 * width with no error anywhere.
 *
 * ⚠️ *SUPERSEDED: "`Table` takes no `style` prop, so the number sits on a block
 * wrapper inside the scroller and the table's own `w-full` fills it."* **`Table`
 * takes `style` now**, so the figure goes straight onto the table and the spacer
 * `<div>` is gone. Only the wrapper moved; the Tailwind rule above is why this is
 * a number in the first place and is untouched.
 *
 * The figures are the `w-*` classes below in px (`w-36` = 144, `w-48` = 192,
 * `w-28` = 112), with the fluid Cycle column carrying the remainder — so with
 * every column shown they still add up to the 880 this table has always used.
 */
const COLUMN_WIDTHS: Record<string, number> = {
  cycleName: 144,
  startDate: 144,
  endDate: 144,
  createdAt: 144,
  modifiedBy: 192,
  [ACTIONS_KEY]: 112,
};

/** Header width classes — static literals, so Tailwind emits them. */
const HEADER_CLASS: Record<string, string> = {
  startDate: 'w-36',
  endDate: 'w-36',
  createdAt: 'w-36',
  modifiedBy: 'w-48',
};

const BODY_CLASS: Record<string, string> = {
  startDate: 'text-xs whitespace-nowrap text-fg-muted tabular',
  endDate: 'text-xs whitespace-nowrap text-fg-muted tabular',
  createdAt: 'text-xs whitespace-nowrap text-fg-muted',
};

const SKELETON_CLASS: Record<string, string> = {
  cycleName: 'h-3 w-32',
  startDate: 'h-3 w-24',
  endDate: 'h-3 w-24',
  createdAt: 'h-3 w-24',
  modifiedBy: 'h-3 w-28',
};

/**
 * The column manager's label for a key — read off `CYCLE_COLUMNS` rather than
 * written out a second time, so the popover and the header can never disagree
 * about what a column is called.
 */
function labelOf(key: string): string {
  if (key === ACTIONS_KEY) return 'Actions';
  return CYCLE_COLUMNS.find((column) => column.key === key)?.label ?? key;
}

/**
 * `modifiedBy === null` is the one reliable "untouched since insert" signal
 * across all three catalog resources. `modifiedAt` is not — on intakes it is
 * NOT NULL and equals `createdAt` on a brand-new row.
 */
function EditedCell({ cycle }: { cycle: Cycle }) {
  if (cycle.modifiedBy === null) {
    return <span className="text-xs text-fg-subtle">Never edited</span>;
  }
  return (
    <span className="text-xs text-fg-muted">
      {cycle.modifiedAt ? formatDateOnly(cycle.modifiedAt) : 'Edited'}
      <span className="text-fg-subtle"> by {cycle.modifiedBy}</span>
    </span>
  );
}

/**
 * Everything a row renderer needs to place a frozen cell, passed as one object
 * so the header, the body and the skeleton cannot drift on which column is
 * frozen or how far in it sits.
 */
interface FrozenModel {
  frozenSideOf: (key: string) => FrozenSide | undefined;
  frozenOffsets: Record<string, number>;
}

/**
 * ⚠️ **Returns an EMPTY object for an unfrozen column, not `frozen: undefined`
 * with an offset beside it.** `TH`/`TD` only emit a `left`/`right` when `frozen`
 * is set, and an unfrozen cell that became a positioned one would paint over the
 * table's collapsed borders for no reason at all.
 */
function frozenPropsFor(
  key: string,
  { frozenSideOf, frozenOffsets }: FrozenModel,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const frozen = frozenSideOf(key);
  if (frozen === undefined) return {};
  return { frozen, frozenOffset: frozenOffsets[key] ?? 0 };
}

/*
 * ⚠️ *SUPERSEDED: a local `HeadCell`, which re-implemented `FilterableTH`'s body
 * so that a frozen header could hand `frozen`/`frozenOffset` to `TH` — the two
 * props `FilterableTHProps` did not carry at the time.* **`FilterableTH`
 * forwards both now**, so the copy is deleted and the shared component is used
 * directly. `aria-sort` is unchanged by the deletion: the branch that computed
 * it here was byte-identical to the one in `ColumnFilter.tsx`, which is the
 * whole reason a second copy was worth removing rather than keeping in step.
 */

function CyclesTableHead({
  columnOrder,
  showActions,
  headerFor,
  headRowRef,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  headerFor: (key: string) => FilterableTHProps;
  headRowRef: React.RefObject<HTMLTableRowElement | null>;
  frozen: FrozenModel;
}) {
  return (
    <THead>
      {/*
        The row has to be the measured element because a frozen column's offset
        is the summed RENDERED width of the columns outside it, and this table
        is auto layout — a `w-*` on a `<th>` is a request its content can
        outvote, so the widths are knowable only from the DOM.

        ⚠️ *SUPERSEDED: the ref went through `{...({ ref } as object)}`, because
        `TRProps` extended `HTMLAttributes`, which carries no `ref`.* **`TRProps`
        declares `ref` now**, so the cast is gone and a wrong ref type is a build
        error again rather than something `as object` waves through.
      */}
      <TR ref={headRowRef}>
        {columnOrder.map((key) =>
          key === ACTIONS_KEY ? (
            /* Pinned to the right edge of the scroller. This table is 880px
               wide against a content area of the viewport minus the ~256px
               sidebar, so unpinned the whole column sat past the edge from
               ~1150 down — measured on production at 1024, where Edit and
               Delete were both off screen and `elementFromPoint` returned
               nothing at all.

               ⚠️ It keeps `pinned` rather than becoming `frozen="right"`:
               `Table.tsx` says `pinned` IS the one-column special case of
               freezing at offset zero, and this column is forced last below so
               that stays exactly true. */
            <TH key={key} numeric pinned className={showActions ? 'w-28' : 'w-20'}>
              Actions
            </TH>
          ) : (
            <FilterableTH
              key={key}
              {...headerFor(key)}
              className={HEADER_CLASS[key]}
              {...frozenPropsFor(key, frozen)}
            />
          ),
        )}
      </TR>
    </THead>
  );
}

function TableSkeleton({
  columnOrder,
  showActions,
  frozen,
}: {
  columnOrder: readonly string[];
  showActions: boolean;
  frozen: FrozenModel;
}) {
  return (
    <TBody>
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <TR key={i}>
          {columnOrder.map((key) =>
            key === ACTIONS_KEY ? (
              <TD key={key} numeric pinned>
                <div className="flex justify-end gap-1.5">
                  {showActions && <Skeleton className="h-6 w-6" />}
                  {showActions && <Skeleton className="h-6 w-6" />}
                </div>
              </TD>
            ) : (
              <TD key={key} {...frozenPropsFor(key, frozen)}>
                <Skeleton className={SKELETON_CLASS[key] ?? 'h-3 w-24'} />
              </TD>
            ),
          )}
        </TR>
      ))}
    </TBody>
  );
}

/** One column's cell body. Keyed the same way as the widths and the header. */
function CycleCell({ column, row }: { column: string; row: Cycle }) {
  switch (column) {
    case 'startDate':
      return <>{formatDateOnly(row.startDate)}</>;
    case 'endDate':
      return <>{formatDateOnly(row.endDate)}</>;
    case 'createdAt':
      return (
        <>
          {formatDateOnly(row.createdAt)}
          <span className="block text-2xs text-fg-subtle">by {row.createdBy}</span>
        </>
      );
    case 'modifiedBy':
      return <EditedCell cycle={row} />;
    default:
      return null;
  }
}

function ListSkeleton() {
  return (
    <ul className="m-0 list-none p-0">
      {Array.from({ length: SKELETON_ROWS }, (_, i) => (
        <li
          key={i}
          className="flex items-start justify-between gap-3 border-b border-border px-4 py-3 last:border-b-0"
        >
          <div className="grid flex-1 gap-1.5">
            <Skeleton className="h-3 w-28" />
            <Skeleton className="h-2.5 w-40" />
          </div>
          <Skeleton className="h-4 w-10" />
        </li>
      ))}
    </ul>
  );
}

export function CyclesPage() {
  const { ability } = useAuth();
  const [result, setResult] = useState<Paginated<Cycle> | null>(null);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  /** `null` closed, `{}` create, `{ cycle }` edit. */
  const [formState, setFormState] = useState<{ cycle?: Cycle } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Cycle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);

  const rows = useMemo(() => result?.data ?? [], [result]);
  const filters = useTableFilters(rows, CYCLE_COLUMNS);

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const sort = useMemo(() => parseSort(new URLSearchParams(searchKey)), [searchKey]);
  const sortMember = sort?.key ?? null;
  const sortDirection = sort?.direction ?? null;

  /** Writes the sort into the URL and returns to page 1 of the re-ordered list. */
  const changeSort = useCallback(
    (member: CycleListSort, direction: SortDirection) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('sort', member);
          next.set('order', direction);
          return next;
        },
        { replace: true },
      );
      setPage(1);
    },
    [setSearchParams],
  );

  /** `filters.headerFor` with the URL-backed sort laid over each funnel column. */
  const headerFor = useCallback(
    (key: string): FilterableTHProps => {
      const base = filters.headerFor(key);
      const member = SORT_MEMBER_BY_KEY[key];
      if (member === undefined || base.filter === undefined) return base;
      return {
        ...base,
        filter: {
          ...base.filter,
          sort: {
            direction: sortMember === member ? sortDirection : null,
            onSort: (direction) => changeSort(member, direction),
          },
        },
      };
    },
    [filters, sortMember, sortDirection, changeSort],
  );

  const columnLayout = useColumnLayout('cycles', COLUMN_KEYS, LOCKED_KEYS, PINNED_KEYS);
  const headRowRef = useRef<HTMLTableRowElement | null>(null);

  /**
   * The visible columns in render order, with the actions lane forced last.
   *
   * ⚠️ **`ColumnManager` locks hiding and MOVING a locked key, but it does not
   * stop another column being moved PAST one** — "Move down" on the
   * second-to-last row is enabled, and one more press puts that column after
   * Actions. Actions carries `pinned`, which is hard-coded `right-0`, so it
   * would then paint over whatever had overtaken it. Pinning the lane to the end
   * of the render order keeps `pinned` exactly equal to `frozen="right"` at
   * offset zero, which is the identity `Table.tsx` states.
   */
  const columnOrder = useMemo(
    () => [...columnLayout.visibleKeys.filter((key) => key !== ACTIONS_KEY), ACTIONS_KEY],
    [columnLayout.visibleKeys],
  );

  /*
   * ⚠️ *SUPERSEDED: a local `frozenSideOf` that reported Actions as right-frozen
   * whatever the stored layout said, so the right-hand stack counted the pinned
   * lane and a second right-frozen column did not compute offset zero on top of
   * it.* **`useFrozenOffsets` takes `pinnedKeys` and does exactly that
   * internally**, so the wrapper is gone and the layout's own answer is passed
   * through untouched.
   *
   * The reason it existed is worth keeping, because nothing on screen reports
   * it: two cells sharing the right edge are each fully opaque, so one simply
   * disappears behind the other and the table reads as having lost a column.
   * That hazard now lives in the hook — see `PINNED_KEYS`.
   *
   * Rendering never asks about the actions lane anyway: the header, the body and
   * the skeleton all branch on `ACTIONS_KEY` before reaching `frozenPropsFor`,
   * and that lane keeps `pinned` rather than becoming `frozen="right"`.
   */
  const frozenOffsets = useFrozenOffsets(
    headRowRef,
    columnOrder,
    columnLayout.frozenSideOf,
    PINNED_KEYS,
  );

  const frozen = useMemo<FrozenModel>(
    () => ({ frozenSideOf: columnLayout.frozenSideOf, frozenOffsets }),
    [columnLayout.frozenSideOf, frozenOffsets],
  );

  /** See `COLUMN_WIDTHS` — a number, never a computed `min-w-[…]` class. */
  const minWidth = useMemo(
    () => columnOrder.reduce((sum, key) => sum + (COLUMN_WIDTHS[key] ?? 0), 0),
    [columnOrder],
  );

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      return api
        .listCycles(
          {
            page,
            perPage: PER_PAGE,
            ...(sortMember !== null && sortDirection !== null
              ? { sort: sortMember, order: sortDirection }
              : {}),
          },
          signal,
        )
        .then((data) => {
          setResult(data);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof Error ? caught.message : 'Failed to load cycles');
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [page, sortMember, sortDirection],
  );

  /*
   * No debounce delay: nothing here is typed, so the only trigger is paging —
   * the column filters never re-query, because this endpoint has nothing to
   * query with. The fetch is still deferred a tick, because `load` flips the
   * loading flag synchronously and doing that inside the effect body cascades
   * a render.
   */
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => void load(controller.signal), 0);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [load]);

  /*
   * Cycles are admin-only to write, and this is enforced server-side however
   * generous the permission set looks — the router carries
   * `requireRole(...ADMIN_ROLES)`, so an `advanced_user` holding
   * `cycles.create` still gets a 403. Asking `ability` keeps that a single call
   * site for the day enforcement moves to the permission model.
   */
  const canCreate = ability.canCreate('cycles');
  const canUpdate = ability.canUpdate('cycles');
  const canDelete = ability.canDelete('cycles');
  const showActions = canUpdate || canDelete;

  /**
   * Why the one locked column cannot be hidden, in the reader's terms.
   *
   * ⚠️ **It has to follow `showActions`, and on this table that is the COMMON
   * branch rather than the edge.** Cycles are admin-only to write, so for a
   * viewer, a `basic_user` and an `advanced_user` alike the lane renders a dash
   * and holds no controls at all — naming Edit and Delete to those readers would
   * describe buttons they cannot see. The fallback `ColumnManager` supplies is a
   * bare "cannot be hidden", which states the refusal without the reason.
   *
   * This is a **why-this-is-disabled** sentence, which is one of the four kinds
   * the no-helper-text rule keeps: without it a locked checkbox and a broken one
   * are the same thing on screen.
   */
  const lockReasonOf = useCallback(
    (key: string): string | undefined => {
      if (key !== ACTIONS_KEY) return undefined;
      return showActions
        ? 'Actions holds the Edit and Delete controls for each row, so it cannot be hidden'
        : 'Actions is the per-row control lane, so it is kept even where you have no controls on these rows';
    },
    [showActions],
  );

  const visibleRows = filters.visibleRows;
  const hiddenByFilters = rows.length - visibleRows.length;
  const total = result?.meta.total ?? 0;
  const totalPages = Math.max(result?.meta.totalPages ?? 1, 1);
  const firstRow = total === 0 ? 0 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + rows.length - 1;
  const initialLoad = loading && !result;

  function openDelete(cycle: Cycle) {
    setDeleteError(null);
    setPendingDelete(cycle);
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteCycle(pendingDelete.id);
      setPendingDelete(null);
      if (rows.length === 1 && page > 1) setPage((p) => p - 1);
      else await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        /*
         * The backend refuses rather than cascading, and its message names the
         * count ("Cycle 8 still has 4 intake(s)"). It is shown as-is and paired
         * with a way to go and deal with them, because the user cannot act on
         * the refusal from here.
         */
        setDeleteError(
          <>
            {caught.message}{' '}
            <Link
              to={`/intakes?cycleId=${pendingDelete.id}`}
              className="font-medium underline underline-offset-2"
            >
              Review its intakes
            </Link>{' '}
            first — nothing was deleted.
          </>,
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        // Already gone — the outcome the user wanted, so treat it as success.
        setPendingDelete(null);
        if (rows.length === 1 && page > 1) setPage((p) => p - 1);
        else await load();
      } else if (caught instanceof ApiError && caught.status === 403) {
        setDeleteError(
          `${denialReason(ability, 'delete', 'cycles') ?? 'The server refused that delete.'} Nothing was deleted — ask an administrator.`,
        );
      } else {
        setDeleteError(caught instanceof Error ? caught.message : 'Delete failed');
      }
    } finally {
      setDeleting(false);
    }
  }

  function handleSaved() {
    const wasCreate = !formState?.cycle;
    setFormState(null);
    if (wasCreate && page !== 1) setPage(1);
    else void load();
  }

  const countLabel = initialLoad ? 'Loading…' : `${total} ${total === 1 ? 'cycle' : 'cycles'}`;
  const hiddenLabel =
    hiddenByFilters > 0 ? ` — ${hiddenByFilters} hidden by column filters on this page` : '';

  const newCycleButton = canCreate ? (
    <Button variant="primary" leadingIcon={<PlusIcon />} onClick={() => setFormState({})}>
      New cycle
    </Button>
  ) : null;

  const emptyState =
    rows.length === 0 ? (
      <CyclesEmptyState hasError={Boolean(error)} action={newCycleButton} />
    ) : (
      <PageFilterEmptyState pageRows={rows.length} onClear={filters.clearAll} />
    );

  return (
    <>
      <PageHeader
        eyebrow="Catalog"
        title="Cycles"
        actions={newCycleButton}
      />

      <Panel>
        <PanelHeader
          title="All cycles"
          actions={
            /* Hidden below `md` because that is where the table itself is
               replaced by the card list, and a column control over a surface
               with no columns is an affordance whose only outcome is nothing.
               It is a wrapper rather than `hidden md:inline-flex` on the button:
               `hidden` and the Button's own `inline-flex` are the same CSS
               property, so which wins would be decided by the emitted
               stylesheet's order rather than by the order they are written. */
            <div className="hidden md:block">
              <ColumnManager
                layout={columnLayout}
                labelOf={labelOf}
                lockedKeys={LOCKED_KEYS}
                lockReasonOf={lockReasonOf}
                pinnedKeys={PINNED_KEYS}
              />
            </div>
          }
        />

        <p role="status" aria-live="polite" className="sr-only">
          {countLabel}
          {hiddenLabel}
        </p>

        {error && (
          <Notice
            tone="danger"
            role="alert"
            title={<>Couldn&rsquo;t load cycles.</>}
            className="mx-4 my-3"
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={cx(
            'transition-opacity duration-150 ease-out motion-reduce:transition-none',
            loading && result && 'opacity-60',
          )}
        >
          <div className="md:hidden">
            {initialLoad ? (
              <ListSkeleton />
            ) : visibleRows.length === 0 ? (
              emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.id} className="border-b border-border px-4 py-3 last:border-b-0">
                    <p className="m-0 text-sm font-medium text-fg [overflow-wrap:anywhere]">
                      {row.cycleName}
                    </p>
                    <p className="mt-1 text-xs text-fg-muted tabular">
                      {formatDateOnly(row.startDate)} → {formatDateOnly(row.endDate)}
                    </p>
                    <p className="mt-1 text-2xs text-fg-subtle">
                      Created {formatDateOnly(row.createdAt)} by {row.createdBy}
                    </p>
                    <div className="mt-1">
                      <EditedCell cycle={row} />
                    </div>
                    {showActions && (
                      <div className="mt-2.5 flex items-center justify-end gap-2">
                        {canUpdate && (
                          <Button
                            variant="secondary"
                            size="sm"
                            leadingIcon={<PencilIcon />}
                            onClick={() => setFormState({ cycle: row })}
                          >
                            Edit
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            variant="danger"
                            size="sm"
                            leadingIcon={<TrashIcon />}
                            onClick={() => openDelete(row)}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {/*
              🔴 **ONE MOUNT, THREE STATES — this was three separate `<Table>`s.**
              The skeleton, the headers-only empty view and the data table now
              differ only in what follows the shared `<THead>`, and that is
              load-bearing rather than tidy: `useFrozenOffsets` measures the
              header ROW and re-measures through a `ResizeObserver` attached to
              it, so a header that unmounts and remounts when the data lands
              leaves the observer watching a detached row. Offsets would then
              freeze at whatever the SKELETON measured — and the first column is
              fluid, so a skeleton bar and a real cycle name genuinely give
              different widths. Keeping one element at one position in the tree
              keeps the same `<tr>` throughout, and the observer live.

              The three states are otherwise unchanged: headers stay mounted
              when column filters have emptied the page, so the filter that did
              it is still reachable to undo, and nothing renders at all when the
              server itself returned no rows.
            */}
            {(initialLoad || rows.length > 0) && (
              <TableWrap frozenHeader>
                {/* The floor is a number because it moves with the visible
                    columns — see `COLUMN_WIDTHS`. It sits on the table itself
                    now; the spacer `<div>` that used to carry it is gone. */}
                <Table style={{ minWidth }}>
                  <CyclesTableHead
                    columnOrder={columnOrder}
                    showActions={showActions}
                    headerFor={headerFor}
                    headRowRef={headRowRef}
                    frozen={frozen}
                  />
                  {initialLoad ? (
                    <TableSkeleton
                      columnOrder={columnOrder}
                      showActions={showActions}
                      frozen={frozen}
                    />
                  ) : visibleRows.length > 0 ? (
                    <TBody>
                      {visibleRows.map((row) => (
                        <TR key={row.id} interactive>
                          {columnOrder.map((key) =>
                            key === 'cycleName' ? (
                              /* Still the row header wherever it sits — the
                                 name is what identifies the row, not its
                                 position. */
                              <TH key={key} scope="row" {...frozenPropsFor(key, frozen)}>
                                <span className="[overflow-wrap:anywhere]">{row.cycleName}</span>
                              </TH>
                            ) : key === ACTIONS_KEY ? (
                              <TD key={key} numeric pinned>
                                <div className="flex justify-end gap-1.5">
                                  {showActions ? (
                                    <>
                                      {canUpdate && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<PencilIcon />}
                                          aria-label={`Edit ${row.cycleName}`}
                                          title={`Edit ${row.cycleName}`}
                                          onClick={() => setFormState({ cycle: row })}
                                        />
                                      )}
                                      {canDelete && (
                                        <Button
                                          variant="danger"
                                          size="sm"
                                          iconOnly
                                          leadingIcon={<TrashIcon />}
                                          aria-label={`Delete ${row.cycleName}`}
                                          title={`Delete ${row.cycleName}`}
                                          onClick={() => openDelete(row)}
                                        />
                                      )}
                                    </>
                                  ) : (
                                    <span className="text-xs text-fg-subtle">—</span>
                                  )}
                                </div>
                              </TD>
                            ) : (
                              <TD
                                key={key}
                                className={BODY_CLASS[key]}
                                {...frozenPropsFor(key, frozen)}
                              >
                                <CycleCell column={key} row={row} />
                              </TD>
                            ),
                          )}
                        </TR>
                      ))}
                    </TBody>
                  ) : null}
                </Table>
              </TableWrap>
            )}
            {!initialLoad && visibleRows.length === 0 && emptyState}
          </div>
        </div>

        {result && (
          <PanelFooter>
            <p className="m-0 text-center text-xs text-fg-muted tabular sm:text-left">
              {total === 0 ? 'No cycles to show' : `Showing ${firstRow}–${lastRow} of ${total}`}
              {hiddenByFilters > 0 && (
                <span className="text-fg-subtle"> · {hiddenByFilters} hidden by column filters</span>
              )}
            </p>
            <div className="flex items-center gap-2">
              <span className="hidden text-xs text-fg-subtle tabular sm:inline">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                leadingIcon={<ChevronIcon direction="left" />}
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="flex-1 sm:flex-none"
                trailingIcon={<ChevronIcon direction="right" />}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </PanelFooter>
        )}
      </Panel>

      <CycleFormModal
        open={formState !== null}
        onClose={() => setFormState(null)}
        cycle={formState?.cycle}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
          setDeleteError(null);
        }}
        onConfirm={() => void confirmDelete()}
        title={pendingDelete ? `Delete ${pendingDelete.cycleName}?` : 'Delete cycle?'}
        description="The cycle will be deleted and can't be recovered. A cycle that still has intakes can't be deleted."
        confirmLabel="Delete cycle"
        loading={deleting}
        error={deleteError}
      />
    </>
  );
}

/**
 * Distinct from "no cycles": the server returned rows and the column filters —
 * which only ever see the loaded page — hid all of them. Saying so is the whole
 * point; otherwise it reads as an empty table.
 */
function PageFilterEmptyState({ pageRows, onClear }: { pageRows: number; onClear: () => void }) {
  return (
    <EmptyState
      icon={<CycleIcon />}
      title="No rows on this page match the column filters"
      description={`Filters apply to the ${pageRows} rows loaded here. Try another page.`}
      action={
        <Button variant="secondary" size="sm" onClick={onClear}>
          Clear column filters
        </Button>
      }
    />
  );
}

function CyclesEmptyState({ hasError, action }: { hasError: boolean; action: ReactNode }) {
  if (hasError) {
    return (
      <EmptyState
        icon={<CycleIcon />}
        title="Nothing to show"
        description="The cycle list couldn’t be loaded — see the message above."
      />
    );
  }

  return (
    <EmptyState
      icon={<CycleIcon />}
      title="No cycles yet"
      description="Add the first business year for intakes to hang off."
      action={action}
    />
  );
}
